import React from 'react'
import { formatRelative } from 'date-fns'
import Linkify from 'linkify-react'
import styled, { useTheme } from 'styled-components'
import { useParams } from 'react-router-dom'

import useScreenSizes from 'src/stories/hooks/useScreenSizes'
import DisplayMedia from 'src/components/DisplayMedia'
import { CommsSourceTypeKey } from 'src/lib/Constants'

import {
  AngiLeadsIcon,
  PhoneIcon,
  ReviewIcon,
  FeedbackIcon,
  CompanyIcon,
  FacebookMessengerIcon,
  GoogleIcon as GoogleConversationalIcon,
  ThumbtackIcon,
} from 'src/stories/assets'

import LoadingSpinner from 'src/stories/LoadingSpinner'
import { displayUserFriendlyMessage } from 'src/contexts/MhContext/utils'
import { generateLocationUrl } from 'src/utils'
import { UseLocationRouteParams } from 'src/utils/interfaces'
import { ConversationEventType } from 'src/client/interfaces/Conversations'

const sourceTypeConfig = {
  system: {
    alignment: 'center',
  },
  outgoing: {
    alignment: 'flex-end',
  },
  incoming: {
    alignment: 'flex-start',
  },
}

interface SharedProps {
  sourceType: CommsSourceTypeKey
  isAutomatedMessage: boolean
  sentViaLabel?: string
  isDesktop?: boolean
  errorMessage?: string
  isWarning?: boolean
  isEmptyBackground?: boolean
  isSimplified?: boolean
  multipleImages?: boolean
}

const StyledMessageItemContainer = styled.div<SharedProps>(
  ({ theme, sourceType, isDesktop, isSimplified }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignSelf: sourceTypeConfig[sourceType].alignment,
    marginTop: theme.space(isSimplified ? 3 : 6),
    maxWidth: isDesktop ? '70%' : '95%',
  })
)

const StyledMessageName = styled.h2(({ theme }) => ({
  margin: 0,
  fontWeight: 500,
  marginRight: theme.space(3),
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  display: 'inline-block',
  maxWidth: 'calc(80%)',
}))

const StyledTimestamp = styled.p(({ theme }) => ({
  margin: 0,
  color: theme.colors.base_40,
}))

const StyledBubbleTitle = styled.p<SharedProps>(
  ({ theme, isAutomatedMessage }) => ({
    marginTop: 0,
    marginBottom: theme.space(1),
    color: isAutomatedMessage ? theme.colors.primary_1 : theme.colors.base_100,
    fontWeight: 500,
  })
)

const StyledMessageHeader = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: theme.space(2),
}))

const StyledBubble = styled.div<SharedProps>(
  ({
    theme,
    sourceType,
    isAutomatedMessage,
    errorMessage,
    isWarning,
    isEmptyBackground,
    multipleImages,
  }) => ({
    display: `${multipleImages ? 'flex' : 'block'}`,
    flexDirection: `${multipleImages ? 'column' : 'row'}`,
    alignSelf: sourceTypeConfig[sourceType].alignment,
    padding: `${theme.space(3)} ${theme.space(4)}`,
    borderRadius: '8px',
    fontSize: '1.4rem',
    lineHeight: '20px',
    border: `${errorMessage ? '2px' : '1px'} solid ${
      errorMessage
        ? theme.colors.critical
        : isAutomatedMessage
        ? theme.colors.primary_2
        : sourceType === 'outgoing'
        ? theme.colors.positive
        : isWarning
        ? theme.colors.critical
        : theme.colors.base_40
    }`,
    backgroundColor: isEmptyBackground
      ? theme.colors.base_0
      : isAutomatedMessage
      ? theme.colors.primary_1_10
      : sourceType === 'outgoing'
      ? theme.colors.positive_15
      : isWarning
      ? theme.colors.critical_15
      : theme.colors.base_5,
    color: isAutomatedMessage
      ? theme.colors.base_40
      : sourceType === 'outgoing'
      ? theme.colors.positive
      : isWarning
      ? theme.colors.critical
      : theme.colors.base_40,
  })
)

const StyledLinkify = styled(Linkify)(({ theme }) => ({
  a: {
    color: theme.colors.primary_2,
    lineBreak: 'anywhere',
  },
  // Accounts for sent via labels
  span: {
    color: theme.colors.primary_1,
    fontWeight: 500,
    fontSize: '1.2rem',
  },
  margin: 0,
  whiteSpace: 'pre-wrap',
}))

const StyledErrorMessage = styled.p(({ theme }) => ({
  color: theme.colors.critical,
  marginBottom: theme.space(2),
  marginTop: theme.space(1),
  fontSize: '1.2rem',
  fontWeight: '500',
}))

interface MessageBubbleProps extends React.PropsWithChildren {
  dataCy: string
  contactName: string
  date: string
  /**
   * It can be system, outgoing or incoming message
   */
  sourceType: CommsSourceTypeKey
  eventType?: ConversationEventType
  media?: { contentType: string; url: string }[]
  errorMessage?: string
  isWarning?: boolean
  isSimplified?: boolean
  isEmptyBackground?: boolean
  isAutomatedMessage?: boolean
  sentViaLabel?: string
  icon?:
    | 'Homeadvisor'
    | 'Review'
    | 'Feedback'
    | 'Hibu'
    | 'Phone'
    | 'Facebook'
    | 'GoogleMessages'
    | 'Thumbtack'
    | 'LiveReceptionist'
  showAsLoading?: boolean

  // at least one of `bodyDescription` or `body` must be provided
  bodyDescription?: string
  body?: string | JSX.Element
}

const MessageBubble: React.FC<MessageBubbleProps> = React.forwardRef<
  HTMLDivElement,
  MessageBubbleProps
>(
  (
    {
      body,
      dataCy,
      contactName,
      date,
      sourceType,
      eventType,
      bodyDescription,
      media,
      errorMessage,
      isAutomatedMessage = false,
      sentViaLabel,
      isWarning = false,
      isSimplified = false,
      isEmptyBackground = false,
      icon,
      showAsLoading = false,
      children,
    },
    ref
  ) => {
    const theme = useTheme()
    const { isLargeScreen: isLargeDesktop } = useScreenSizes()

    const { locationId, merchantId } = useParams<UseLocationRouteParams>()

    const failureMessage = displayUserFriendlyMessage(
      errorMessage,
      eventType,
      sourceType
    )

    const buildErrorMessage = (_failureMessage: {
      message: string
      link?: string
      wordWithLink?: string
    }) => {
      if (failureMessage.link) {
        const { message, link, wordWithLink } = _failureMessage
        const [preLinkMessage, postLinkMessage] = message.split(wordWithLink!)

        return (
          <>
            {preLinkMessage}
            <a
              href={generateLocationUrl(merchantId!, locationId!, link)}
              target="_blank"
              rel="noreferrer"
            >
              {wordWithLink}
            </a>
            {postLinkMessage}
          </>
        )
      } else {
        return failureMessage.message
      }
    }

    let titleIcon
    const iconStyles: React.CSSProperties = {
      position: 'relative',
      top: '2px',
    }

    switch (icon) {
      case 'Homeadvisor':
        titleIcon = <AngiLeadsIcon style={iconStyles} />
        break
      case 'Facebook':
        titleIcon = <FacebookMessengerIcon style={iconStyles} />
        break
      case 'GoogleMessages':
        titleIcon = <GoogleConversationalIcon style={iconStyles} />
        break
      case 'Thumbtack':
        titleIcon = <ThumbtackIcon style={iconStyles} />
        break
      case 'Review':
        titleIcon = <ReviewIcon style={iconStyles} />
        break
      case 'Feedback':
        titleIcon = <FeedbackIcon style={iconStyles} />
        break
      case 'Hibu':
        titleIcon = <CompanyIcon width={18} height={18} style={iconStyles} />
        break
      case 'LiveReceptionist':
        titleIcon = <PhoneIcon style={iconStyles} />
        break
      case 'Phone':
        titleIcon = (
          <PhoneIcon style={iconStyles} fill={theme.colors.base_100} />
        )
        break
      default:
        break
    }

    return (
      <StyledMessageItemContainer
        data-cy={dataCy}
        isDesktop={isLargeDesktop}
        sourceType={sourceType}
        isAutomatedMessage={isAutomatedMessage}
        isSimplified={isSimplified}
        errorMessage={errorMessage}
        ref={ref}
      >
        {!isSimplified && (
          <StyledMessageHeader>
            <StyledMessageName data-cy={dataCy + '-name'}>
              {titleIcon} {contactName}
            </StyledMessageName>
            <StyledTimestamp data-cy={dataCy + '-timestamp'}>
              {formatRelative(new Date(date), new Date())}
            </StyledTimestamp>
          </StyledMessageHeader>
        )}
        <StyledBubble
          data-cy={dataCy + '-message'}
          isWarning={isWarning}
          sourceType={sourceType}
          isAutomatedMessage={isAutomatedMessage}
          errorMessage={errorMessage}
          isEmptyBackground={isEmptyBackground}
          multipleImages={!!(media?.length && media.length > 1)}
        >
          {showAsLoading ? (
            <LoadingSpinner />
          ) : (
            <div>
              {bodyDescription && (
                <StyledBubbleTitle
                  sourceType={sourceType}
                  isAutomatedMessage={isAutomatedMessage}
                >
                  {bodyDescription}
                </StyledBubbleTitle>
              )}
              {media?.length && media.length > 0 ? (
                <div>
                  {body && (
                    <StyledLinkify tagName="p" options={{ target: '_blank' }}>
                      {body}
                    </StyledLinkify>
                  )}
                  {media.map((md, idx) => (
                    <React.Fragment key={`${dataCy}-media-${idx}`}>
                      {md.url && (
                        <DisplayMedia
                          url={md.url}
                          contentType={md.contentType}
                          dataCy={`${dataCy}-media-${idx}`}
                          includesText={!!body}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              ) : React.Children.toArray(children).some((child) =>
                  React.isValidElement(child)
                ) ? (
                <div>{children}</div>
              ) : (
                <StyledLinkify tagName="p" options={{ target: '_blank' }}>
                  {body}
                </StyledLinkify>
              )}
              {/* Future: If & when we want to add more than Thumbtack to sentViaLabel, we must confirm
                 whether the exact same design should be used for both. We might change the condition to
                 `&& sentViaLabel`, or maybe even add more configuration options & make it a sub-component. */}
              {isAutomatedMessage && sentViaLabel?.includes('Thumbtack') && (
                <>
                  {/* Just a div to make a 8px margin top effect */}
                  <div style={{ height: theme.space(2) }}></div>
                  <StyledLinkify tagName="p" options={{ target: '_blank' }}>
                    <span>{`Delivered to lead as a ${sentViaLabel} Reply`}</span>
                  </StyledLinkify>
                  <StyledLinkify tagName="a" options={{ target: '_blank' }}>
                    <a
                      href={generateLocationUrl(
                        merchantId!,
                        locationId!,
                        `/settings#section-settings-instant-responders`
                      )}
                      target="_blank"
                      style={{ fontSize: theme.space(3) }}
                      rel="noreferrer"
                    >
                      Edit Instant Response message
                    </a>
                  </StyledLinkify>
                </>
              )}
            </div>
          )}
        </StyledBubble>

        {errorMessage && (
          <StyledErrorMessage>
            {buildErrorMessage(failureMessage)}
          </StyledErrorMessage>
        )}
      </StyledMessageItemContainer>
    )
  }
)

export default MessageBubble
